import { QueryClient } from '@tanstack/react-query';
import {
  CubeQuery,
  CubeQueryRecord,
  RecordQueryDirectData,
  DebugExtractorInfo,
  RecordingSetupInfo,
} from '../types/cubeQueryTypes';
import { CubejsApi } from './RecordingState';
import { QueryStore } from './QueryStore';
import { QueryRecorder } from './QueryRecorder';
import { QueryAnalyzer } from './QueryAnalyzer';
import { QueryExporter } from './QueryExporter';
import { TabStateManager } from './TabStateManager';

class CubeQueryExtractorService {
  private static instance: CubeQueryExtractorService;

  private queryStore: QueryStore;

  private queryRecorder: QueryRecorder;

  private queryAnalyzer: QueryAnalyzer;

  private queryExporter: QueryExporter;

  private tabStateManager: TabStateManager;

  private constructor() {
    this.queryStore = new QueryStore();
    this.tabStateManager = new TabStateManager();
    this.queryRecorder = new QueryRecorder(this.queryStore, this.tabStateManager);
    this.queryAnalyzer = new QueryAnalyzer(this.queryStore);
    this.queryExporter = new QueryExporter(this.queryStore, this.queryAnalyzer);
  }

  static getInstance(): CubeQueryExtractorService {
    if (!CubeQueryExtractorService.instance) {
      CubeQueryExtractorService.instance = new CubeQueryExtractorService();
    }
    return CubeQueryExtractorService.instance;
  }

  subscribe(listener: () => void): () => void {
    return this.queryStore.subscribe(listener);
  }

  setCubejsApi(api: CubejsApi): void {
    this.queryRecorder.setCubejsApi(api);
  }

  setQueryClient(client: QueryClient): void {
    this.queryRecorder.setQueryClient(client);
  }

  extractQueryKeyFromReactQuery(cubeQuery: CubeQuery): string[] | undefined {
    return this.queryRecorder.extractQueryKeyFromReactQuery(cubeQuery);
  }

  startRecording(): void {
    this.queryRecorder.startRecording();
  }

  stopRecording(): void {
    this.queryRecorder.stopRecording();
  }

  getQueries(): CubeQueryRecord[] {
    return this.queryStore.getQueries();
  }

  getQueriesByComponent(): Record<string, CubeQueryRecord[]> {
    return this.queryAnalyzer.getQueriesByComponent();
  }

  getQueriesByPage(): Record<string, CubeQueryRecord[]> {
    return this.queryAnalyzer.getQueriesByPage();
  }

  recordQueryDirect(data: RecordQueryDirectData): void {
    this.queryRecorder.recordQueryDirect(data);
  }

  setActiveTab(tabIndex: number | string): void {
    this.tabStateManager.setActiveTab(tabIndex);
  }

  getActiveTab(): string {
    return this.tabStateManager.getActiveTab();
  }

  getIsRecording(): boolean {
    return this.queryStore.getRecording();
  }

  getMaxQueries(): number {
    return this.queryStore.getMaxQueries();
  }

  getCurrentQueriesCount(): number {
    return this.queryStore.getCurrentCount();
  }

  clearQueries(): void {
    this.queryStore.clearQueries();
  }

  deleteQuery(queryId: string): void {
    this.queryStore.deleteQuery(queryId);
  }

  deleteQueriesByComponent(componentName: string): void {
    this.queryStore.deleteQueriesByComponent(componentName);
  }

  deleteQueriesByPage(pagePath: string): void {
    this.queryStore.deleteQueriesByPage(pagePath);
  }

  exportAsJSON(): string {
    return this.queryExporter.exportAsJSON();
  }

  updatePagePath(pagePath: string): void {
    this.queryRecorder.updatePagePath(pagePath);
  }

  async refreshQuery(queryId: string): Promise<void> {
    return this.queryRecorder.refreshQuery(queryId);
  }

  recordQuery(query: CubeQuery): void {
    this.queryRecorder.recordQuery(query);
  }

  addQueryRecord(record: CubeQueryRecord): void {
    this.queryStore.addQuery(record);
  }

  generateRequestId(): string {
    return this.queryRecorder.generateRequestId();
  }

  getDebugInfo(): DebugExtractorInfo {
    return {
      isRecording: this.getIsRecording(),
      queriesCount: this.getCurrentQueriesCount(),
      queries: this.getQueries(),
      maxQueries: this.getMaxQueries(),
    };
  }

  getRecordingSetupInfo(): RecordingSetupInfo {
    return this.queryRecorder.getRecordingSetupInfo();
  }
}

export const cubeQueryExtractor = CubeQueryExtractorService.getInstance();

if (typeof window !== 'undefined') {
  window.cubeQueryExtractor = cubeQueryExtractor;
}

export { CubeQueryExtractorService };
